/**
 * @file: useSearchOrders.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useInfiniteListWithCursor } from '@euler/functions/useInfiniteListWithCursor';
import { IdType } from '@euler/model/common';
import { SearchOrderParams } from '@euler/model/order/SearchOrderParams';
import { SearchOrderResultItem } from '@euler/model/order/SearchOrderResultItem';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { useCallback } from 'react';

export const useSearchOrders = (params: SearchOrderParams, limit = 15) => {
  const { orderService } = useServiceFactory();

  const { fetch, fetchMore, fetchSinceLastError, ...list } =
    useInfiniteListWithCursor<SearchOrderResultItem, IdType>({
      limit,
      query: async nextKey => {
        if (!Object.keys(params).length) {
          return {
            data: [],
            cursor: undefined,
          };
        }
        return await orderService.searchOrders(params, nextKey, limit);
      },
    });

  const onScrollToBottom = useCallback(() => {
    void fetchMore().catch(onErrorIgnore);
  }, [fetchMore]);

  return {
    onScrollToBottom,
    fetch,
    fetchMore,
    fetchSinceLastError,
    ...list,
  };
};
